// src/services/imageService.js

import axios from 'axios';
import store from '@/store';
import html2canvas from 'html2canvas';
import { withTimeout, H2C_TIMEOUT_MS, cloneOnly } from '@/utils/withTimeout';

/**
 * 이미지 처리와 관련된 공통 유틸리티 함수들을 포함하는 서비스
 * - xiangji-image-editor 연동 및 이미지 번역/OCR 처리 기능 포함
 * - 이미지 병합 및 비동기 처리 기능 강화
 */
const imageService = {
  // 요청 ID를 저장하는 캐시 맵
  requestIdMap: new Map(),
  /**
   * 이미지 URL에서 Blob 객체로 다운로드
   * HTTP URL은 자동으로 HTTPS로 변환 (Mixed Content 오류 방지)
   * @param {string} url - 이미지 URL
   * @returns {Promise<Blob>} - 이미지 Blob 객체
   */
  async downloadImage(url) {
    console.log('다운로드imageUrl : ',url)
    try {
      // HTTP URL을 HTTPS로 자동 변환 (Mixed Content 오류 방지)
      let finalUrl = url;
      if (url.startsWith('http://')) {
        finalUrl = url.replace('http://', 'https://');
        console.log('[imageService] HTTP → HTTPS 자동 변환:', finalUrl);
      }

      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'image/jpeg' },
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`이미지 다운로드 실패: ${response.status} ${response.statusText}`);
      }
      return await response.blob();
    } catch (error) {
      console.error('이미지 다운로드 중 오류:', error);
      throw error;
    }
  },
  /**
   * HTML 요소를 이미지로 변환
   * @param {Object} options - HTML 생성 옵션
   * @param {Object} options.labels - 라벨 데이터
   * @param {Object} options.labelNameMap - 라벨 이름 매핑 (선택 사항)
   * @returns {Promise<string>} - 생성된 이미지의 Base64 데이터 URL
   */
  async generateHtmlToImage(options) {
    const { labels, labelNameMap = {} } = options;

    // HTML 컨테이너 생성
    const container = document.createElement('div');
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.fontSize = '14px';
    container.style.padding = '20px';
    container.style.border = '1px solid #ccc';
    container.style.width = '500px';
    container.style.background = 'white';

    // 라벨 추가
    for (const key in labels) {
      if (labels[key]) {
        const labelDiv = document.createElement('div');
        labelDiv.style.marginBottom = '10px';

        const labelName = document.createElement('strong');
        const displayName = labelNameMap[key] || key;
        labelName.innerText = displayName + ': ';
        labelDiv.appendChild(labelName);

        const labelValue = document.createElement('span');
        labelValue.innerText = labels[key];
        labelDiv.appendChild(labelValue);

        container.appendChild(labelDiv);
      }
    }

    // body에 추가
    document.body.appendChild(container);

    try {
      // html2canvas로 캡처 — 복제는 라벨 div·그 조상·head 의 같은 출처 css 만(cloneOnly · 윈 3469 PR261021-359DEB)
      const canvas = await withTimeout(html2canvas(container, { ignoreElements: cloneOnly(container) }), H2C_TIMEOUT_MS, '한글 표시사항 그리기');
      return canvas.toDataURL('image/png');
    } finally {
      // 항상 DOM에서 제거
      document.body.removeChild(container);
    }
  },

  /**
   * Blob 객체를 Base64 문자열로 변환
   * @param {Blob} blob - 이미지 Blob 객체
   * @returns {Promise<string>} - Base64 인코딩된 이미지 문자열
   */
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  /**
   * 파일 객체를 Base64 문자열로 변환
   * @param {File} file - 파일 객체
   * @returns {Promise<string>} - Base64 인코딩된 문자열
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  },

  /**
   * Base64 이미지를 S3에 업로드
   * @param {string} imageData - Base64 인코딩된 이미지 데이터
   * @param {string} filename - 저장할 파일 이름
   * @param {string} type - 이미지 타입 (main, additional, detail 등)
   * @returns {Promise<string>} - 업로드된 이미지 URL
   */
  async uploadImageToS3(params) {
    // 객체 또는 개별 파라미터 지원
    let imageData, filename, type;

    if (typeof params === 'object' && params !== null) {
      // 객체 형태로 전달된 경우
      imageData = params.imageData;
      filename = params.filename;
      type = params.type;
    } else {
      // 개별 파라미터로 전달된 경우 (이전 버전 호환)
      imageData = arguments[0];
      filename = arguments[1];
      type = arguments[2];
    }
    try {
      // 디버깅 - 호출 스택 추적을 위한 로깅 추가
      console.log(`[S3 업로드 디버깅] uploadImageToS3 호출됨 - 타입: ${type}, 파일명: ${filename}`);
      console.trace('[S3 업로드 디버깅] 호출 스택 추적');

      // 이미지 업로드 중 상태 표시를 위한 로딩 메시지 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: `${type} 이미지 업로드 중...`
      });

      // filename이 undefined인 경우 기본값 설정
      if (!filename) {
        console.warn('[S3 업로드 경고] 파일명이 제공되지 않았습니다. 기본 파일명을 사용합니다.');
        const supplierCode = store.state.user.suppliercode || store.state.user.companyid || 'unknown';
        const timestamp = new Date().getTime();
        filename = `${supplierCode}_${type || 'image'}_${timestamp}.jpg`;
      }

      // 파일명에 항상 확장자가 있는지 확인
      if (!filename.match(/\.(jpg|jpeg|png|gif)$/i)) {
        filename += '.jpg'; // 확장자 추가
      }

      // 공급자 코드 추가 (filename에 이미 포함되어 있지 않다면)
      const supplierCode = store.state.user.suppliercode || store.state.user.companyid;
      if (!filename.startsWith(supplierCode)) {
        filename = `${supplierCode}_${filename}`;
      }

      // API 호출로 S3 업로드
      const response = await axios.post('/api/rocket-registration/upload-image', {
        imageData,
        filename,
        type,
        companyId: store.state.user.companyid
      });

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      if (response.data.요청결과 === "success" && response.data.imageUrl) {
        console.log(`[S3 업로드 성공] 타입: ${type}, URL: ${response.data.imageUrl}`);
        // 객체 형태로 반환하도록 수정
        return {
          imageUrl: response.data.imageUrl,
          success: true,
          // 대표이미지(type 'main')는 서버가 400~8000px 로 맞춘 실제 크기를 준다(PEACH 360×360 반려)
          ...(response.data.width && response.data.height ? { width: response.data.width, height: response.data.height } : {})
        };
      } else {
        console.error('[S3 업로드 실패]:', response.data.message || '알 수 없는 오류');
        throw new Error(response.data.message || 'S3 업로드 실패');
      }
    } catch (error) {
      console.error('S3 업로드 예외:', error);
      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });
      throw error;
    }
  },

  /**
   * 단일 이미지 번역 요청 (xiangji API)
   * @param {string} imageUrl - 번역할 이미지 URL
   * @returns {Promise<Object>} - 번역 결과 객체
   */
  async translateImage(imageUrl) {
    try {
      console.log('translateImage 호출 - 이미지 URL:', imageUrl.substring(0, 50) + '...');

      // 로딩 상태 표시
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '이미지 번역 중...'
      });

      // 요청 시간 기록
      const startTime = Date.now();
      console.log('API 요청 시작:', new Date(startTime).toISOString());

      const res = await axios.post('/api/aiPage/getImageTranslateV2', [{imgUrl: imageUrl}]);

      // 응답 시간 기록
      const endTime = Date.now();
      console.log('API 응답 수신:', new Date(endTime).toISOString(), '응답 시간:', (endTime - startTime) + 'ms');

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      // API 응답 상세 로깅
      console.log('API 응답 데이터 전체 구조:', JSON.stringify(res.data, null, 2));
      console.log('API 응답 요약:', {
        요청결과: res.data.요청결과,
        결과길이: res.data.result ? res.data.result.length : 0,
        에러메시지: res.data.error || '없음'
      });

      // 이미지 URL 추출 테스트
      if (res.data.요청결과 === '성공' && res.data.result && res.data.result.length > 0) {
        const testResult = res.data.result[0];
        console.log('응답에서 이미지 URL 추출 테스트:', {
          Url: testResult.Data?.Url || '없음',
          SslUrl: testResult.Data?.SslUrl || '없음',
          RmUrl: testResult.Data?.RmUrl || '없음',
          SslRmUrl: testResult.Data?.SslRmUrl || '없음'
        });
      }

      if (res.data.요청결과 === '성공' && res.data.result && res.data.result.length > 0) {
        const result = res.data.result[0];
        result.imgUrl = imageUrl; // 원본 이미지 URL 기록

        // 결과 상세 로깅
        console.log('번역 결과 전체 데이터:', JSON.stringify(result, null, 2));
        console.log('번역 결과 구조 분석:', {
          requestId: result.requestId || result.RequestId || (result.Data && result.Data.RequestId) || '없음',
          translatedImageUrl: result.translatedImageUrl || '없음',
          // API 응답 구조에 따른 이미지 URL 필드
          Url: (result.Data && result.Data.Url) || '없음',  // 번역된 이미지
          SslUrl: (result.Data && result.Data.SslUrl) || '없음',  // 번역된 이미지 (https)
          RmUrl: (result.Data && result.Data.RmUrl) || '없음',  // 텍스트 제거된 이미지
          SslRmUrl: (result.Data && result.Data.SslRmUrl) || '없음',  // 텍스트 제거된 이미지 (https)
          // 다른 형식 호환
          TranslateUrl: result.TranslateUrl || (result.Data && result.Data.TranslateUrl) || '없음',
          SslTranslateUrl: result.SslTranslateUrl || (result.Data && result.Data.SslTranslateUrl) || '없음',
          Data존재: !!result.Data
        });

        return result;
      }

      // 에러 상세 정보
      console.error('번역 실패 상세 정보:', res.data);
      throw new Error(res.data.error || '번역 실패');
    } catch (error) {
      console.error('이미지 번역 오류:', error);

      // 상세한 오류 정보 로깅
      if (error.response) {
        console.error('번역 API 응답 오류:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: JSON.stringify(error.response.data)
        });
      } else if (error.request) {
        console.error('번역 API 요청 오류 (응답 없음):', error.request);
      } else {
        console.error('번역 요청 설정 중 오류:', error.message);
      }

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });
      throw error;
    }
  },

  /**
   * 이미지에서 텍스트 제거 (xiangji 이미지 번역 API 사용)
   * @param {string} imageUrl - 원본 이미지 URL
   * @returns {Promise<string>} - 텍스트가 제거된 이미지 URL
   */
  async removeTextFromImage(imageUrl) {
    console.log('removeTextFromImage 호출:', imageUrl);

    try {
      // 번역 API 호출 - 내부적으로 로딩 상태를 설정하지 않도록 수정
      console.log('translateImage API 호출 시작');
      const result = await this.translateImageWithoutLoading(imageUrl);
      console.log('translateImage API 응답:', result);

      // 텍스트가 제거된 이미지 URL 추출
      if (result) {
        // API 응답 구조 분석
        console.log('API 응답 구조 분석:', {
          'Data 객체 존재': !!result.Data,
          'Data.RmUrl 존재': !!(result.Data && result.Data.RmUrl),
          'Data.SslRmUrl 존재': !!(result.Data && result.Data.SslRmUrl),
          '최상위 RmUrl 존재': !!result.RmUrl,
          '최상위 SslRmUrl 존재': !!result.SslRmUrl,
          'RequestId 존재': !!result.RequestId,
          'requestId 존재': !!result.requestId
        });

        // 텍스트 제거 이미지 URL 가져오기 (Data 객체 안에 있음)
        const textRemovedImageUrl =
          (result.Data && result.Data.SslRmUrl) ||
          (result.Data && result.Data.RmUrl)

        // RequestId는 대문자로 시작하는 형태로 전달됨
        const requestId = result.RequestId || result.requestId || '';

        console.log('텍스트 제거 결과 파싱:', {
          '텍스트제거 URL': textRemovedImageUrl || '없음',
          'requestId': requestId || '없음'
        });

        if (textRemovedImageUrl) {
          console.log('텍스트 제거 성공. 제거된 이미지 URL 반환:', textRemovedImageUrl);
          return {
            url: textRemovedImageUrl,
            requestId: requestId,
            RmUrl: result.Data ? result.Data.RmUrl : result.RmUrl,
            SslRmUrl: result.Data ? result.Data.SslRmUrl : result.SslRmUrl,
            success: true
          };
        }
      }

      // 이미지 처리 실패 시 원본 URL 반환
      console.warn("이미지 텍스트 제거 결과가 없습니다. 원본 이미지를 사용합니다.");
      return {
        url: imageUrl,
        requestId: '',
        success: false
      };
    } catch (error) {
      console.error('텍스트 제거 오류:', error);
      // 오류 발생 시 원본 URL 반환
      console.log('텍스트 제거 실패. 원본 이미지 반환:', imageUrl);
      return {
        url: imageUrl,
        requestId: '',
        success: false,
        error: error.message
      };
    }
  },

  // 로딩 상태를 설정하지 않는 번역 함수
  async translateImageWithoutLoading(imageUrl) {
    try {
      const res = await axios.post('/api/aiPage/getImageTranslateV2', [{imgUrl: imageUrl}]);

      if (res.data.요청결과 === '성공' && res.data.result && res.data.result.length > 0) {
        const result = res.data.result[0];
        result.imgUrl = imageUrl; // 원본 이미지 URL 기록
        return result;
      }

      throw new Error(res.data.error || '번역 실패');
    } catch (error) {
      console.error('이미지 번역 오류(로딩 표시 없음):', error);
      throw error;
    }
  },

  /**
   * 배치 번역 결과 조회 (xiangji API)
   * @param {string[]} requestIds - 번역 요청 ID 배열
   * @returns {Promise<Object>} - 배치 조회 결과
   */
  async getBatchQueryResult(requestIds) {
    try {
      // 로딩 상태 표시
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: 'OCR 정보 조회 중...'
      });

      const res = await axios.post('/api/aiPage/getImageTranslateBatchQuery', requestIds);

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      if (res.data.요청결과 === '성공') {
        return res.data.result;
      }

      throw new Error(res.data.error || '배치 조회 실패');
    } catch (error) {
      console.error('배치 조회 오류:', error);
      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });
      throw error;
    }
  },

  /**
   * OCR 정보 기반 이미지 커스터마이징 (xiangji API)
   * @param {Object} data - OCR 커스터마이징 데이터
   * @returns {Promise<Object>} - 커스터마이징 결과
   */
  async getImageOCR(data) {
    try {
      // 로딩 상태 표시
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '이미지 OCR 처리 중...'
      });

      const res = await axios.post('/api/aiPage/getImageOCR', data);

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      if (res.data.요청결과 === '성공') {
        return res.data.result;
      }

      throw new Error(res.data.error || 'OCR 처리 실패');
    } catch (error) {
      console.error('OCR 처리 오류:', error);
      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });
      throw error;
    }
  },

  /**
   * 이미지 병합 및 번역 처리
   * @param {string[]} imageUrls - 병합할 이미지 URL 배열
   * @returns {Promise<Object>} - 병합 및 번역 결과
   */
  async mergeAndTranslateImages(imageUrls) {
    try {
      // 로딩 상태 표시
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '이미지 병합 및 번역 중...'
      });

      // 이미지 로드
      const loadImage = (url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'Anonymous';

          img.onload = () => {
            console.log(`이미지 로드 성공: ${url.substring(0, 50)}...`);
            resolve(img);
          };

          img.onerror = (e) => {
            console.error(`이미지 로드 실패: ${url.substring(0, 50)}...`, e);
            reject(new Error(`이미지 로드 실패: ${url.substring(0, 50)}...`));
          };

          // 타임아웃 설정
          const timeout = setTimeout(() => {
            console.error(`이미지 로드 타임아웃: ${url.substring(0, 50)}...`);
            img.src = ''; // 로드 중단
            reject(new Error(`이미지 로드 타임아웃: ${url.substring(0, 50)}...`));
          }, 15000); // 15초 후 타임아웃

          // 이벤트 핸들러에서 타임아웃 해제
          img.onload = () => {
            clearTimeout(timeout);
            console.log(`이미지 로드 성공: ${url.substring(0, 50)}...`);
            resolve(img);
          };

          img.onerror = (e) => {
            clearTimeout(timeout);
            console.error(`이미지 로드 실패: ${url.substring(0, 50)}...`, e);
            reject(new Error(`이미지 로드 실패: ${url.substring(0, 50)}...`));
          };

          console.log(`이미지 로드 시도: ${url.substring(0, 50)}...`);
          img.src = url;
        });
      };

      // 로드된 이미지 개수 확인
      console.log(`이미지 로드 시도: ${imageUrls.length}개`);

      // 이미지 로드
      const images = await Promise.all(imageUrls.map(loadImage));
      console.log(`이미지 로드 완료: ${images.length}개`);

      // 이미지 크기 정보 출력
      const imageSizes = images.map((img, idx) => ({
        index: idx,
        width: img.width,
        height: img.height,
        url: imageUrls[idx].substring(0, 30) + '...'
      }));
      console.log('로드된 이미지 크기 정보:', imageSizes);

      // 캔버스 생성 및 이미지 그리기
      const width = Math.max(...images.map(img => img.width));
      const height = images.reduce((sum, img) => sum + img.height, 0);

      console.log('캔버스 생성 크기:', { width, height });

      // 너무 큰 이미지는 문제가 될 수 있으므로 크기 확인
      if (width > 5000 || height > 15000) {
        console.warn('캔버스 크기가 너무 큼, 문제 발생 가능:', { width, height });
      }

      let dataUrl = '';

      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('캔버스 컨텍스트를 가져올 수 없음');
        }

        console.log('캔버스 생성 완료, 이미지 그리기 시작');

        let yOffset = 0;
        for (const [index, img] of images.entries()) {
          try {
            ctx.drawImage(img, 0, yOffset);
            yOffset += img.height;
            console.log(`이미지 ${index+1} 그리기 완료`);
          } catch (drawError) {
            console.error(`이미지 ${index+1} 그리기 오류:`, drawError);
            throw drawError;
          }
        }

        // 캔버스 데이터 URL 변환
        console.log('이미지 병합 완료, 데이터 URL 변환 시작');
        dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        console.log('데이터 URL 변환 완료, 길이:', dataUrl.length);
      } catch (canvasError) {
        console.error('캔버스 처리 중 오류:', canvasError);
        throw canvasError;
      }

      // 번역 요청
      console.log('API 요청 시작: 병합된 이미지 번역');
      const startApiTime = Date.now();
      let res;

      try {
        res = await axios.post('/api/aiPage/getImageTranslateV2', [{imgUrl: dataUrl}]);

        // 응답 시간 기록
        const endApiTime = Date.now();
        console.log('API 응답 수신 시간:', (endApiTime - startApiTime) + 'ms');

        // API 응답 로깅
        console.log('병합 이미지 번역 API 응답:', {
          요청결과: res.data.요청결과,
          결과데이터존재: res.data.result && res.data.result.length > 0
        });
      } catch (apiError) {
        console.error('병합 이미지 번역 API 오류:', apiError);

        if (apiError.response) {
          console.error('API 응답 오류 정보:', {
            status: apiError.response.status,
            statusText: apiError.response.statusText,
            data: JSON.stringify(apiError.response.data)
          });
        } else if (apiError.request) {
          console.error('API 요청 오류 (응답 없음):', apiError.request);
        }

        throw apiError;
      }

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      if (res && res.data && res.data.요청결과 === '성공' && res.data.result && res.data.result.length > 0) {
        console.log('병합 이미지 번역 성공:', {
          requestId: res.data.result[0].requestId || '없음',
          translatedImageUrl: res.data.result[0].translatedImageUrl ? '있음' : '없음'
        });

        return {
          mergedResult: res.data.result[0],
          originalImages: images,
          mergedDataUrl: dataUrl
        };
      } else {
        // API 호출은 성공했지만 결과가 실패인 경우
        console.error('병합 이미지 번역 실패 (API 결과):', res ? res.data : 'API 응답 없음');
        throw new Error((res && res.data && res.data.error) || '병합 이미지 번역 실패');
      }
    } catch (error) {
      // 오류 세부 정보 출력
      console.error('이미지 병합 및 번역 오류:', error);

      // 오류 상세 정보 로깅 강화
      if (error.response) {
        console.error('API 응답 오류 상세:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: JSON.stringify(error.response.data),
          headers: error.response.headers
        });
      }

      // 오류 객체 자체 상세 출력
      console.error('오류 객체 전체 내용:', JSON.stringify(error, Object.getOwnPropertyNames(error)));

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      // 더 자세한 오류 메시지 제공
      const errorMessage = error.message || (error.response && error.response.data ?
        (typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)) :
        '이미지 병합 및 번역 중 오류가 발생했습니다');

      throw new Error(errorMessage);
    }
  },

  /**
   * 외부 에디터 초기화 및 연동 (xiangji-image-editor)
   * @deprecated 이 메서드는 더 이상 사용되지 않습니다. ImageEditor.vue 컴포넌트에서 직접 통신하는 방식으로 변경되었습니다.
   * @returns {Promise<Object>} - 에디터 응답 메시지
   */
  // eslint-disable-next-line no-unused-vars
  initExternalEditor(requestId, imageUrl) {
    console.warn('initExternalEditor 메서드는 더 이상 사용되지 않습니다.');
    return Promise.reject(new Error('이 메서드는 더 이상 사용되지 않습니다. ImageEditor.vue 컴포넌트를 사용하세요.'));
  },

  /**
   * 이미지 크기 가져오기 (width, height)
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<{width: number, height: number}>} - 이미지 크기
   */
  getImageDimensions(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => {
        reject(new Error('이미지 로드 실패'));
      };
      img.src = imageUrl;
    });
  },

  /**
   * 이미지 파일 크기 추정 (Base64 문자열 기반)
   * @param {string} dataUrl - Base64 이미지 URL
   * @returns {string} - 포맷팅된 파일 크기 (KB, MB 등)
   */
  estimateFileSize(dataUrl) {
    if (dataUrl.startsWith('data:')) {
      const base64Length = dataUrl.split(',')[1].length;
      const fileSizeBytes = Math.round((base64Length * 0.75));

      if (fileSizeBytes < 1024) {
        return `${fileSizeBytes} B`;
      } else if (fileSizeBytes < 1024 * 1024) {
        return `${Math.round(fileSizeBytes / 1024)} KB`;
      } else {
        return `${(fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`;
      }
    }
    return '알 수 없음';
  },

  /**
   * 이미지 URL이 유효한지 확인
   * @param {string} url - 확인할 이미지 URL
   * @returns {Promise<boolean>} - 이미지 URL 유효 여부
   */
  async checkImageUrl(url) {
    if (!url) return false;

    try {
      // HEAD 요청으로 이미지가 존재하는지 확인
      const response = await axios.head(url, { timeout: 3000 });
      return response.status === 200;
    } catch (error) {
      console.warn(`이미지 URL 확인 실패: ${url}`, error.message);
      return false;
    }
  },

  /**
   * 이미지 크기 조정
   * @param {string} imageUrl - 원본 이미지 URL
   * @param {number} width - 너비
   * @param {number} height - 높이
   * @returns {Promise<string>} - 크기 조정된 이미지의 Data URL
   */
  async resizeImage(imageUrl, width, height) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const resizedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
        resolve(resizedImageUrl);
      };
      img.onerror = (e) => {
        console.log(e)
        reject(new Error('이미지 리사이즈 실패'));
      };
      img.src = imageUrl;
    });
  },

  /**
   * HTML에서 이미지 URL 추출
   * @param {string} html - HTML 문자열
   * @returns {string[]} - 추출된 이미지 URL 배열
   */
  extractImagesFromHtml(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgElements = doc.querySelectorAll('img');
    return Array.from(imgElements)
      .map(img => img.src)
      .filter(src => src && src.length > 10);
  },

  /**
   * Blob을 DataURL로 변환
   * @param {Blob} blob - 변환할 Blob
   * @returns {Promise<string>} - DataURL
   */
  blobToDataURL(blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function (e) {
        resolve(e.target.result);
      };
      reader.readAsDataURL(blob);
    });
  }
};

export default imageService;